// DetailHeader — el encabezado único de los cajones de detalle (Cartera, Cobranza, Clientes):
// quién es, en qué estado está, su id copiable y tres datos de un vistazo.
import type { ReactNode } from 'react';
import { CopyableId } from '@/kit';
import { cn } from '@/lib/cn';

export type DetailHeaderTone = 'neutral' | 'success' | 'danger' | 'warning' | 'info';

const PILL_TONE_CLASS: Record<DetailHeaderTone, string> = {
    success: 'bg-success-soft text-success',
    danger: 'bg-danger-soft text-danger',
    warning: 'bg-warning-soft text-warning',
    info: 'bg-info-soft text-info',
    neutral: 'bg-muted text-muted-foreground',
};

/** Pastilla de estado con el chasis del encabezado. */
export function DetailHeaderPill({
    tone = 'neutral',
    title,
    children,
}: {
    tone?: DetailHeaderTone;
    title?: string;
    children: ReactNode;
}) {
    return (
        <span
            title={title}
            className={cn(
                'inline-flex flex-none items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold',
                title !== undefined && 'cursor-help',
                PILL_TONE_CLASS[tone],
            )}
        >
            {children}
        </span>
    );
}

/** Un dato del encabezado: responde una sola pregunta. */
export interface DetailHeaderFact {
    key: string;
    label: string;
    /** Qué mide el dato, al pasar el cursor por su rótulo. */
    title?: string;
    value: ReactNode;
    sub?: ReactNode;
    /** 0–100. Presente ⇒ se pinta la barra de avance bajo el valor. */
    progressPct?: number;
    tone?: 'danger';
    /** Un valor de texto largo parte en dos líneas en vez de cortarse. */
    wrap?: boolean;
}

export interface DetailHeaderProps {
    /** Contexto sobre el nombre, como «Cuota 3 de 12» en Cobranza. */
    eyebrow?: ReactNode;
    name: ReactNode;
    /** El nombre todavía carga. */
    busy?: boolean;
    /** Nivel y estado: van pegados al nombre. */
    badges?: ReactNode;
    /** Teléfono, tienda, fecha… Un elemento ausente (`null`/`undefined`/`''`) se omite sin hueco. */
    subItems?: readonly ReactNode[];
    /** La sub-línea parte en renglones en vez de cortarse con «…». */
    subWrap?: boolean;
    idLabel: string;
    idValue: string;
    /** Acción junto al id, como el enlace a la ficha del crédito. */
    idAction?: ReactNode;
    /** Aviso de carga o falla que afecta a los datos. */
    note?: ReactNode;
    facts: readonly DetailHeaderFact[];
    /** Columnas de los datos; por defecto, una por dato. Con 2, cuatro datos van en dos filas. */
    factColumns?: 2 | 3 | 4;
    ariaLabel?: string;
}

const FACT_COLUMNS_CLASS: Record<2 | 3 | 4, string> = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
};

/** El texto completo para el `title` cuando el nodo es texto plano; un nodo armado se describe solo. */
const textTitleOf = (node: ReactNode): string | undefined =>
    typeof node === 'string' || typeof node === 'number' ? String(node) : undefined;

const present = (item: ReactNode): boolean =>
    item !== null && item !== undefined && item !== false && item !== '';

export function DetailHeader({
    eyebrow,
    name,
    busy,
    badges,
    subItems = [],
    subWrap,
    idLabel,
    idValue,
    idAction,
    note,
    facts,
    factColumns,
    ariaLabel,
}: DetailHeaderProps) {
    const columns = factColumns ?? (facts.length === 4 ? 4 : 3);
    const shown = subItems.filter(present);

    return (
        <header
            aria-label={ariaLabel}
            className="flex-none border-b border-border bg-card px-4 py-3"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    {eyebrow === undefined ? null : (
                        <div className="mb-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                            {eyebrow}
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h2
                            aria-busy={busy || undefined}
                            className="m-0 min-w-0 truncate text-[16px] font-bold leading-tight"
                        >
                            {name}
                        </h2>
                        {badges}
                    </div>
                    {shown.length === 0 ? null : (
                        <p
                            data-testid="detail-header-sub"
                            className={cn(
                                'mt-1 text-[11px] text-muted-foreground',
                                subWrap ? 'break-words leading-snug' : 'truncate',
                            )}
                        >
                            {shown.map((item, index) => (
                                <span key={index}>
                                    {index > 0 ? ' · ' : null}
                                    {item}
                                </span>
                            ))}
                        </p>
                    )}
                </div>
                <div className="flex max-w-[45%] flex-none items-center gap-1">
                    {/* El rótulo queda para el lector de pantalla; a la vista va solo el id. */}
                    <CopyableId
                        label={idLabel}
                        value={idValue}
                        className="min-w-0 [&>button]:max-w-full [&>button]:gap-1 [&>button]:border-0 [&>button]:bg-transparent [&>button]:p-0 [&>button]:text-[10.5px] [&>button]:text-muted-foreground [&>span]:sr-only"
                    />
                    {idAction}
                </div>
            </div>

            {note === undefined || note === null ? null : (
                <div className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground">
                    {note}
                </div>
            )}

            <dl className={cn('mt-2.5 grid grid-cols-1 gap-2', FACT_COLUMNS_CLASS[columns])}>
                {facts.map((fact) => (
                    <div
                        key={fact.key}
                        data-testid="detail-header-fact"
                        className="flex min-w-0 flex-col rounded-md border border-border bg-muted/30 px-2.5 py-2"
                    >
                        <dt
                            title={fact.title}
                            className={cn(
                                'truncate text-[9.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground',
                                fact.title !== undefined && 'cursor-help',
                            )}
                        >
                            {fact.label}
                        </dt>
                        <dd
                            title={fact.wrap ? undefined : textTitleOf(fact.value)}
                            className={cn(
                                'm-0 mt-0.5 text-[14px] font-bold tabular-nums',
                                fact.wrap ? 'leading-tight' : 'truncate',
                                fact.tone === 'danger' && 'text-danger',
                            )}
                        >
                            {fact.value}
                        </dd>
                        {fact.progressPct === undefined ? null : (
                            <dd className="m-0 mt-1">
                                <div
                                    className="h-1 overflow-hidden rounded-full bg-border"
                                    role="progressbar"
                                    aria-label={fact.label}
                                    aria-valuenow={fact.progressPct}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                >
                                    <div
                                        className="h-full rounded-full bg-primary"
                                        style={{ width: `${String(fact.progressPct)}%` }}
                                    />
                                </div>
                            </dd>
                        )}
                        {fact.sub === undefined ? null : (
                            // Dos renglones como máximo; los puntos suspensivos quedan de último recurso.
                            <dd
                                title={textTitleOf(fact.sub)}
                                className="m-0 mt-0.5 line-clamp-2 break-words text-[10.5px] leading-snug text-muted-foreground"
                            >
                                {fact.sub}
                            </dd>
                        )}
                    </div>
                ))}
            </dl>
        </header>
    );
}
